import React, {useEffect, useState, useMemo} from "react";
import {useNavigate} from "react-router-dom";
import { useOpenPath } from "../../hooks/useOpenPath";
import { translate } from "../../types/EnumTranslations";
import {BaseType} from "../../types/Enums";
import "./LevelPage.css";
import BuildingCustomButton from "../BuildingDetails/BuildingCustomButton";
import {useDocumentTitle} from "../../hooks/useDocumentTitle";
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import InfoPanel from "../../Components/InfoPanel";
import {AREA_ESTIMATE_INFO} from "../../constants/infoTexts";
import VerifiedAreaCheck from "../../Components/VerifiedAreaCheck";
import NestedRiskConstruction from "../../Components/NestedRiskConstruction";
import NestedHazardView from "../../Components/NestedHazardView";
import {useAuthState} from "react-firebase-hooks/auth";
import {auth} from "../../firebase";
import {useUsersData} from "../../Helpers/UsersContext";
import BlueprintUploadModal, { DeferredBlueprintUpload } from "../../Components/BlueprintUploadModal";
import BlueprintRelocationWizard, { RelocationDrawing } from "../../Components/BlueprintRelocationWizard";
import { RelocatableMarker, levelRelocationItems } from "../../Helpers/blueprintRelocation";
import { abortBlueprintRelocation, commitBlueprintRelocation } from "../../api/blueprintRelocationApi";
import { BlueprintLocation } from "../../types/protocols";
import { useStorageUnits } from "../../hooks/storageUnits";
import { useUnits } from "../../hooks/units";
import { Unit } from "../../types/units";
import { unitStatusColor, unitTypeIcon, effectiveUnitLocations, isWholeUnitRoomType, unitDisplayName, unitHoldingRoom, unitMarkerRented, unitsOnLevel } from "../../Helpers/unitUtils";
import { isMultiLevelRoom, roomLevelSpanLabel } from "../../Helpers/roomUtils";
import { Alert } from "react-bootstrap";
import {useLevel} from "../../hooks/level";
import LevelNavigator from "../../Components/LevelNavigator";
import LevelBlueprintSection from "./LevelBlueprintSection";
import LevelHistorySection from "./LevelHistorySection";
import EntityActionsDropdown from "../../Components/EntityActionsDropdown";
import { PageLoader } from "../../Components/LoadingSpinner";
import { ChangeLevelNumberModal } from "./ChangeLevelNumberModal";
import CreateLevelModal from "./CreateLevelModal";
import { LEVEL_BLUEPRINT_ENTITY_TYPE, levelsApi, LevelInput } from "../../api/levelsApi";
import { useParking } from "../../hooks/parking";
import { useFeature } from "../../hooks/useFeatures";
import { hasRoofPlanView } from "../../Helpers/roofPlan";
import { NumberedMarker, NumberedMarkerGlyph } from "../../Components/BlueprintWithMarkers";
import {
    areasOnLevel,
    isSpotRented,
    parkingAreaDisplayName,
    parkingSpotDisplayName,
    sortedParkingSpots,
    spotHasCharger,
    spotStatusColor,
    spotTypeIcon,
} from "../../Helpers/parkingUtils";

const LevelPage: React.FC = () => {
    const navigate = useNavigate();
    const openPath = useOpenPath();
    const [hoveredRoomKey, setHoveredRoomKey] = useState<string | null>(null);
    const [hoveredComponentKey, setHoveredComponentKey] = useState<string | null>(null);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [showBlueprintUpload, setShowBlueprintUpload] = useState<boolean>(false);
    const [showComponents, setShowComponents] = useState<boolean>(false);
    const [showRooms, setShowRooms] = useState<boolean>(true);
    const [showParking, setShowParking] = useState<boolean>(true);
    const [showUnits, setShowUnits] = useState<boolean>(false);
    const [droppedFile, setDroppedFile] = useState<File | null>(null);
    const [dropError, setDropError] = useState<string | null>(null);
    const [showChangeLevelNumberModal, setShowChangeLevelNumberModal] = useState(false);
    const [showCreateLevelModal, setShowCreateLevelModal] = useState(false);
    const [creatingLevel, setCreatingLevel] = useState(false);
    // Drawing replacement in progress: the new drawing is uploaded but not yet
    // current; the wizard collects marker positions and one commit switches.
    const [relocation, setRelocation] = useState<{
        source: RelocationDrawing;
        target: RelocationDrawing;
        items: RelocatableMarker[];
        upload: DeferredBlueprintUpload;
    } | null>(null);
    const [relocationError, setRelocationError] = useState<string | null>(null);

    const {
        buildingKey,
        levelKey,
        level,
        currentBuilding,
        loadingBuilding,
        buildingError,
        fetchBuilding,
        currentBlueprint,
        isNoLevelCase,
        rooms,
        drawnRooms,
        allUniqueLevelComponents,
        componentsForBlueprint,
        totalLevelCeilingArea,
        totalLevelWallArea,
        totalLevelFloorArea,
        totalLevelCeilingAreaExcludingOpenings,
        totalLevelWallAreaExcludingOpenings,
        totalLevelFloorAreaExcludingOpenings,
        utilitiesCount,
        fixedFurnishingsCount,
        riskConstructions,
        hazards,
        allLevels,
        roomFloorAreas,
        roomFloorAreaStates,
        allRoomsFloorAreaVerified,
    } = useLevel();

    // Parking spots of level-mode areas on this level.
    const { areas: parkingAreas } = useParking({ buildingKey });
    // Storage units are keyed by the level's drawing too; they only matter for relocation here.
    const { sortedUnits: storageUnits } = useStorageUnits({ buildingKey });
    // Lägenheter/lokaler are pins only when "Visa hyresobjekt" is on; otherwise their rooms are the markers and
    // the rooms list badges them with the unit's name.
    const { units: rentalUnitsMap, sortedUnits: rentalUnits } = useUnits({ buildingKey });
    const buildingRooms = currentBuilding?.model.structure?.rooms;
    // Features turned off show no markers here; relocation below still moves every marker.
    const parkingEnabled = useFeature('parking');
    const unitsEnabled = useFeature('units');
    const unitsOnThisLevel = useMemo(
        () => (levelKey && !isNoLevelCase && unitsEnabled ? unitsOnLevel(rentalUnits, levelKey, buildingRooms) : []),
        [rentalUnits, levelKey, isNoLevelCase, buildingRooms, unitsEnabled]
    );
    // Pins sit at the centroid of the unit's rooms on this drawing; a unit with no room placed here has none.
    const unitMarkersOnLevel = useMemo((): NumberedMarker[] => {
        const blueprintKey = level?.currentBlueprintKey;
        if (!blueprintKey) return [];
        return unitsOnThisLevel
            .map(unit => ({ unit, blueprintLocations: effectiveUnitLocations(unit, buildingRooms) }))
            .filter(({ blueprintLocations }) => !!blueprintLocations[blueprintKey])
            .map(({ unit, blueprintLocations }) => ({
                key: unit.key,
                number: unit.number,
                color: unitStatusColor(unit),
                icon: unitTypeIcon(unit.kind),
                iconColored: true,
                isRented: unitMarkerRented(unit),
                displayName: unitDisplayName(unit),
                blueprintLocations,
                movable: false,
            }));
    }, [unitsOnThisLevel, buildingRooms, level?.currentBlueprintKey]);
    const parkingOnLevel = useMemo(() => {
        const blueprintKey = level?.currentBlueprintKey;
        const spotAreaKeys = new Map<string, string>();
        const markers: NumberedMarker[] = [];
        if (isNoLevelCase || !levelKey || !blueprintKey || !currentBuilding || !parkingEnabled) return { markers, spotAreaKeys };
        for (const area of areasOnLevel(parkingAreas, currentBuilding.model.structure, levelKey)) {
            for (const spot of sortedParkingSpots(area)) {
                if (!spot.blueprintLocations?.[blueprintKey]) continue;
                const glyphs: NumberedMarkerGlyph[] = [];
                if (spotHasCharger(spot)) glyphs.push('charger');
                if (spot.hasOutlet === true) glyphs.push('outlet');
                spotAreaKeys.set(spot.key, area.key);
                markers.push({
                    key: spot.key,
                    number: spot.number,
                    color: spotStatusColor(spot),
                    icon: spotTypeIcon(spot.type),
                    glyphs,
                    isRented: isSpotRented(spot),
                    displayName: `${parkingSpotDisplayName(spot)} (${parkingAreaDisplayName(area)})`,
                    blueprintLocations: spot.blueprintLocations,
                });
            }
        }
        return { markers, spotAreaKeys };
    }, [parkingAreas, currentBuilding, isNoLevelCase, levelKey, level?.currentBlueprintKey, parkingEnabled]);

    // Parking spots and unit pins share the marker layer; keys are Firebase push keys, so they never collide.
    const handleSpotClick = (spotKey: string, event?: React.MouseEvent) => {
        const areaKey = parkingOnLevel.spotAreaKeys.get(spotKey);
        if (areaKey) {
            openPath(`/building/${buildingKey}/parking/${areaKey}?spot=${encodeURIComponent(spotKey)}`, event);
        } else if (rentalUnitsMap[spotKey] && levelKey) {
            openPath(`/building/${buildingKey}/units?level=${encodeURIComponent(levelKey)}&unit=${encodeURIComponent(spotKey)}`, event);
        }
    };

    const [user] = useAuthState(auth);
    const { users } = useUsersData();

    // Check if current user is admin
    useEffect(() => {
        if (user && users) {
            const currentUser = users.find(u => u.key === user.uid);
            setIsAdmin(currentUser?.admin || false);
        }
    }, [user, users]);

    // Existing level numbers (excluding the current level)
    const existingLevelNumbers = useMemo(() => {
        return allLevels
            .filter(l => l.key !== levelKey)
            .map(l => l.number);
    }, [allLevels, levelKey]);

    // All numbers, for the create-level modal (no level is excluded there)
    const allLevelNumbers = useMemo(() => allLevels.map(l => l.number), [allLevels]);
    // D18: the Takplan pill exists only when the building has roof components or a roof drawing.
    const showRoofPlanPill = useMemo(() => hasRoofPlanView(currentBuilding?.model.structure), [currentBuilding]);

    const handleSaveLevelNumber = async (newNumber: number) => {
        if (!buildingKey || !levelKey) return;

        await levelsApi.updateLevelNumber(buildingKey, levelKey, newNumber);
        await fetchBuilding(buildingKey);
    };

    const handleCreateLevel = async (input: LevelInput) => {
        if (!buildingKey) return;
        setCreatingLevel(true);
        try {
            const level = await levelsApi.createLevel(buildingKey, input);
            await fetchBuilding(buildingKey);
            setShowCreateLevelModal(false);
            navigate(`/building/${buildingKey}/level/${level.key}`);
        } finally {
            setCreatingLevel(false);
        }
    };

    // Set document title based on state
    useDocumentTitle(
        loadingBuilding ? 'Laddar våningsplan...' :
        buildingError ? 'Fel - Våningsplan' :
        !currentBuilding ? 'Våningsplan' :
        (!isNoLevelCase && !level) ? 'Våningsplan' :
        !levelKey ? 'Våningsplan' :
        `${isNoLevelCase ? 'Ingen våning' : (level?.displayName() || 'Våningsplan')}: ${currentBuilding.summary.name}`
    );

    const roomPath = (roomKey: string) => `/building/${buildingKey}/room/${roomKey}`;
    const componentPath = (componentKey: string) => `/building/${buildingKey}/component/${componentKey}`;

    const handleMarkerClick = (roomKey: string, event?: React.MouseEvent) => {
        openPath(roomPath(roomKey), event);
    };

    const handleMouseEnter = (roomKey: string) => {
        setHoveredRoomKey(roomKey);
    };

    const handleMouseLeave = () => {
        setHoveredRoomKey(null);
    };

    const handleComponentClick = (componentKey: string, event?: React.MouseEvent) => {
        openPath(componentPath(componentKey), event);
    };

    const handleComponentMouseEnter = (componentKey: string) => {
        setHoveredComponentKey(componentKey);
    };

    const handleComponentMouseLeave = () => {
        setHoveredComponentKey(null);
    };

    // The drawing node is written; decide whether markers must be moved first.
    const handleDeferredUpload = async (upload: DeferredBlueprintUpload) => {
        if (!buildingKey || !levelKey || !currentBuilding) return;
        setRelocationError(null);
        const currentKey = level?.currentBlueprintKey;
        const items = currentKey && currentBlueprint
            ? levelRelocationItems(currentBuilding, levelKey, currentKey, storageUnits, Object.values(parkingAreas))
            : [];
        if (!currentKey || !currentBlueprint || items.length === 0) {
            // Nothing to move: make the drawing current right away, as before.
            try {
                await commitBlueprintRelocation({ imageKey: upload.key, pointerUpdates: upload.pointerUpdates, placements: [] });
            } catch (err) {
                setRelocationError(err instanceof Error ? err.message : 'Ritningen kunde inte bytas');
            }
            await fetchBuilding(buildingKey);
            return;
        }
        setRelocation({
            source: { image: currentBlueprint, imageKey: currentKey },
            target: { image: upload.blueprint, imageKey: upload.key },
            items,
            upload,
        });
    };

    const handleRelocationCommit = async (placements: Map<string, BlueprintLocation>) => {
        if (!relocation) return;
        await commitBlueprintRelocation({
            imageKey: relocation.target.imageKey,
            pointerUpdates: relocation.upload.pointerUpdates,
            placements: relocation.items.flatMap(item => {
                const location = placements.get(item.id);
                return location ? [{ dbPath: item.dbPath, location }] : [];
            }),
        });
        setRelocation(null);
        if (buildingKey) await fetchBuilding(buildingKey);
    };

    const handleRelocationAbort = async () => {
        if (!relocation) return;
        await abortBlueprintRelocation({ blueprintPath: relocation.upload.blueprintPath, entityType: LEVEL_BLUEPRINT_ENTITY_TYPE });
        setRelocation(null);
    };

    const handleFileDrop = (file: File) => {
        setDroppedFile(file);
        setShowBlueprintUpload(true);
    };

    const handleBlueprintModalClose = () => {
        setShowBlueprintUpload(false);
        setDroppedFile(null);
    };

    // Helper to format area with optional "inkl öppningar" when values differ
    const formatAreaWithOpenings = (total: number, excluding: number, label: string) => {
        if (total <= 0) return { label, value: "Okänd kvm", info: AREA_ESTIMATE_INFO };
        const diff = Math.abs(total - excluding);
        if (diff > 0.01) {
            return { label, value: `${excluding.toFixed(1)} kvm (${total.toFixed(1)} kvm inkl öppningar)`, info: AREA_ESTIMATE_INFO };
        }
        return { label, value: `${total.toFixed(1)} kvm`, info: AREA_ESTIMATE_INFO };
    };

    if (loadingBuilding) return <PageLoader />;
    if (buildingError) return <p>{buildingError}</p>;
    if (!currentBuilding) return <p>Building data missing</p>;
    if (!isNoLevelCase && !level) return <>Level data missing</>;
    if (!levelKey) {
        return <div style={{ padding: "20px" }}>Nivå kunde inte hittas</div>;
    }

    return (
        <>
            <BreadcrumbComponent
                items={[
                    {text: currentBuilding.summary.name ?? 'Byggnad', link: `/building/${buildingKey}`},
                    {text: isNoLevelCase ? 'Ingen våning' : (level?.displayName() || 'Våningsplan')}
                ]}
            />

            <div style={{ padding: "20px", maxWidth: '1640px' }}>
                {relocationError && (
                    <Alert variant="danger" dismissible onClose={() => setRelocationError(null)}>{relocationError}</Alert>
                )}
                <div className="level-title-container">
                    <h3 style={{margin: 0}}>
                        {isNoLevelCase ? 'Ingen våning' : (level?.displayName() || 'Våningsplan')}
                    </h3>
                    <LevelNavigator
                        levels={allLevels}
                        selectedKey={levelKey}
                        levelPath={key => `/building/${buildingKey}/level/${key}`}
                        roofPlan={showRoofPlanPill ? { to: `/building/${buildingKey}/roof` } : undefined}
                    />
                    {!isNoLevelCase && buildingKey && levelKey && (
                        <EntityActionsDropdown
                            buildingKey={buildingKey}
                            entityKey={levelKey}
                            entityType="level"
                            size="large"
                            onCreateLevel={isAdmin ? () => setShowCreateLevelModal(true) : undefined}
                        />
                    )}
                </div>
                <div
                    className="dropshadow"
                    style={{
                        width: '100%',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        padding: '40px',
                        marginBottom: '20px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '60px',
                    }}
                >

                    <div style={{ flex: 1, minWidth: '300px', maxWidth: currentBlueprint ? currentBlueprint.width : undefined }}>
                        <LevelBlueprintSection
                            currentBlueprint={currentBlueprint}
                            currentBlueprintKey={level?.currentBlueprintKey}
                            building={currentBuilding}
                            rooms={drawnRooms}
                            isNoLevelCase={isNoLevelCase}
                            isAdmin={isAdmin}
                            showRooms={showRooms}
                            showComponents={showComponents}
                            onShowRoomsChange={setShowRooms}
                            onShowComponentsChange={setShowComponents}
                            parkingSpots={parkingOnLevel.markers}
                            showParking={showParking}
                            onShowParkingChange={setShowParking}
                            hasUnits={unitsOnThisLevel.length > 0}
                            unitSpots={unitMarkersOnLevel}
                            showUnits={showUnits}
                            onShowUnitsChange={setShowUnits}
                            onSpotClick={handleSpotClick}
                            componentsForBlueprint={componentsForBlueprint}
                            allComponents={allUniqueLevelComponents}
                            hoveredRoomKey={hoveredRoomKey}
                            hoveredComponentKey={hoveredComponentKey}
                            onRoomClick={handleMarkerClick}
                            onRoomMouseEnter={handleMouseEnter}
                            onRoomMouseLeave={handleMouseLeave}
                            onComponentClick={handleComponentClick}
                            componentPath={componentPath}
                            onComponentMouseEnter={handleComponentMouseEnter}
                            onComponentMouseLeave={handleComponentMouseLeave}
                            onUploadClick={() => setShowBlueprintUpload(true)}
                            onFileDrop={handleFileDrop}
                            onFileDropError={setDropError}
                        />
                    </div>

                    <div style={{ width: '280px', flexShrink: 0 }}>
                        <h4 style={{marginBottom: '20px'}}>Rum</h4>
                        {rooms.length > 0 ? (
                            <div>
                                {rooms
                                    .sort((roomA, roomB) => {
                                        if (roomA.number && roomB.number) {
                                            const numA = parseFloat(roomA.number);
                                            const numB = parseFloat(roomB.number);

                                            if (!isNaN(numA) && !isNaN(numB)) {
                                                return numA - numB;
                                            }
                                        }
                                        if (roomA.number && !roomB.number) return -1;
                                        if (!roomA.number && roomB.number) return 1;
                                        return roomA.key.localeCompare(roomB.key);
                                    })
                                    .map((room) => {
                                        const hasCurrentBlueprintLocation = !isNoLevelCase && level?.currentBlueprintKey &&
                                            room.blueprintLocations &&
                                            room.blueprintLocations[level.currentBlueprintKey];

                                        const roomFloorArea = roomFloorAreas.get(room.key) || 0;
                                        const roomFloorAreaText = roomFloorArea > 0 ? `${roomFloorArea.toFixed(1)} kvm` : 'Okänd kvm';
                                        // Golvyta uppmätt på ritning and still current (web#1383)
                                        const roomFloorAreaMeta = roomFloorAreaStates.get(room.key)?.kind === 'verified'
                                            ? <>{roomFloorAreaText}<VerifiedAreaCheck size={13} /></>
                                            : roomFloorAreaText;
                                        // Whole-unit rooms (units plan D13) read as the lägenhet/lokal they are.
                                        const holdingUnit = !unitsEnabled ? undefined : unitHoldingRoom(rentalUnitsMap, room.key);
                                        const unitBadge = holdingUnit && isWholeUnitRoomType(room.type)
                                            ? unitDisplayName(holdingUnit as Unit)
                                            : holdingUnit ? `Tillhör ${unitDisplayName(holdingUnit as Unit)}` : undefined;
                                        // A room spanning several levels says so; it is listed on its primary level only (multi-level rooms D2).
                                        const buildingLevels = currentBuilding?.model.structure?.levels;
                                        const spanText = isMultiLevelRoom(room, buildingLevels) ? [roomLevelSpanLabel(room, buildingLevels)] : [];

                                        return (
                                            <React.Fragment key={room.key}>
                                                <BuildingCustomButton
                                                    title={room.displayName()}
                                                    metadata={[
                                                        unitBadge ?? translate(room.type),
                                                        ...spanText,
                                                        roomFloorAreaMeta,
                                                        ...(hasCurrentBlueprintLocation ? [] : ['Position ej markerad på ritning'])
                                                    ]}
                                                    imagePath={`rooms/${room.type ?? "room_general"}.png`}
                                                    to={roomPath(room.key)}
                                                    onMouseEnter={() => handleMouseEnter(room.key)}
                                                    onMouseLeave={handleMouseLeave}
                                                    isHovered={hoveredRoomKey === room.key}
                                                    className="level-room-button"
                                                />
                                            </React.Fragment>
                                        );
                                    })}
                            </div>
                        ) : (
                            <>Detta våningsplan innehåller inga rum</>
                        )}
                    </div>

                    <div style={{ width: '280px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                            <h4 style={{marginBottom: '20px'}}>Fakta</h4>
                            <InfoPanel items={[
                                {
                                    label: 'Våning',
                                    value: isNoLevelCase ? 'Ingen våning' : (level?.formattedNumber() || '-'),
                                    onEdit: (!isNoLevelCase && isAdmin)
                                        ? () => setShowChangeLevelNumberModal(true)
                                        : undefined,
                                    editTitle: 'Ändra våningsnummer',
                                },
                                {label: 'Eget namn', value: isNoLevelCase ? '-' : (level?.name || '-')},
                                {label: 'Antal rum', value: rooms.length},
                                formatAreaWithOpenings(totalLevelCeilingArea, totalLevelCeilingAreaExcludingOpenings, 'Takyta'),
                                formatAreaWithOpenings(totalLevelWallArea, totalLevelWallAreaExcludingOpenings, 'Väggyta'),
                                {
                                    ...formatAreaWithOpenings(totalLevelFloorArea, totalLevelFloorAreaExcludingOpenings, 'Golvyta'),
                                    // Every room on the level is uppmätt på ritning (web#1383)
                                    ...(allRoomsFloorAreaVerified
                                        ? { info: 'Golvytan i varje rum på våningen är uppmätt på ritning.', valueIcon: <VerifiedAreaCheck /> }
                                        : {}),
                                },
                                {label: 'Fastighetsteknik', value: `${utilitiesCount} st`},
                                {label: translate(BaseType.FixedFurnishing), value: `${fixedFurnishingsCount} st`},
                            ]}/>
                        </div>

                        {(riskConstructions.length > 0 || hazards.length > 0) && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                {riskConstructions.length !== 0 && (
                                    <NestedRiskConstruction
                                        riskConstructions={riskConstructions}
                                        buildingKey={buildingKey || ''}
                                    />
                                )}

                                {hazards.length !== 0 && (
                                    <NestedHazardView
                                        hazards={hazards}
                                        buildingKey={buildingKey || ''}
                                    />
                                )}
                            </div>
                        )}

                        {!isNoLevelCase && buildingKey && levelKey && (
                            <LevelHistorySection
                                buildingKey={buildingKey}
                                levelKey={levelKey}
                                roomKeys={rooms.map(r => r.key)}
                            />
                        )}
                    </div>
                </div>
            </div>

            {!isNoLevelCase && (
                <BlueprintUploadModal
                    isOpen={showBlueprintUpload}
                    onClose={handleBlueprintModalClose}
                    deferPointer
                    onDeferredUpload={handleDeferredUpload}
                    buildingKey={buildingKey || ''}
                    levelKey={levelKey}
                    authorUid={user?.uid || ''}
                    initialFile={droppedFile}
                />
            )}

            {relocation && (
                <BlueprintRelocationWizard
                    building={currentBuilding}
                    source={relocation.source}
                    target={relocation.target}
                    items={relocation.items}
                    onCommit={handleRelocationCommit}
                    onAbort={handleRelocationAbort}
                />
            )}

            {dropError && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                    }}
                    onClick={() => setDropError(null)}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            padding: '30px',
                            borderRadius: '10px',
                            maxWidth: '400px',
                            width: '90%'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Filtyp stöds ej</h3>
                        <p style={{ marginBottom: '20px', color: '#666' }}>{dropError}</p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setDropError(null)}
                                style={{
                                    padding: '10px 20px',
                                    border: 'none',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    borderRadius: '5px',
                                    cursor: 'pointer'
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {level && (
                <ChangeLevelNumberModal
                    show={showChangeLevelNumberModal}
                    onHide={() => setShowChangeLevelNumberModal(false)}
                    currentNumber={level.number}
                    existingNumbers={existingLevelNumbers}
                    onSave={handleSaveLevelNumber}
                />
            )}

            <CreateLevelModal
                show={showCreateLevelModal}
                onHide={() => setShowCreateLevelModal(false)}
                onSubmit={handleCreateLevel}
                existingNumbers={allLevelNumbers}
                submitting={creatingLevel}
            />
        </>
    );
};

export default LevelPage;
